import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEnvelopeOpen, faTrashCan, faXmark } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import {
  IonButton,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
} from '@ionic/angular/standalone';
import { jsonLdSelectId } from '@techniek-team/tt-ngrx';
import { TtDateRangePipe } from '@techniek-team/tt-range';
import { isToday } from 'date-fns';
import { NotificationContract } from '../contracts/notification.contract';

@Component({
  selector: 'app-notification',
  imports: [
    IonItem,
    IonItemSliding,
    IonItemOptions,
    IonItemOption,
    IonButton,
    FaIconComponent,
    TtDateRangePipe,
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationComponent {
  public readonly notification = input.required<NotificationContract>();

  protected readonly notificationId = computed(() => {
    return jsonLdSelectId(this.notification());
  });

  public readonly enableSwipe = input(false);

  public readonly showCloseButton = input(false);

  public readonly remove = output<string>();

  public readonly markAsRead = output<string>();

  public readonly clickAction = output<string | NotificationContract['actions'][0]>();

  public readonly actionTriggered = output<NotificationContract['actions'][0]>();

  public readonly closing = output();

  protected readonly sendAtToday = computed(() => {
    return isToday(new Date(this.notification().sendAt));
  });

  protected readonly sendAtCurrentYear = computed(() => {
    return new Date(this.notification().sendAt).getFullYear() === new Date().getFullYear();
  });

  protected readonly faChevronDown = faChevronDown;

  protected readonly faChevronUp = faChevronUp;

  protected isExpanded = false;

  private readonly bodyElement = viewChild<ElementRef<HTMLParagraphElement>>('bodyText');

  protected readonly isTruncated = signal(false);

  protected readonly canExpand = computed(() => {
    return this.notification().actions.length > 0 || this.isTruncated();
  });

  constructor() {
    afterNextRender(() => {
      this.checkTruncation();
    });
  }

  protected toggleExpand(event: MouseEvent) {
    event.stopPropagation();
    this.isExpanded = !this.isExpanded;
  }

  protected onItemClick() {
    const action = this.notification().clickAction ?? this.notification().actions[0];

    if (action) {
      this.clickAction.emit(action);
    }
  }

  private checkTruncation() {
    const element = this.bodyElement()?.nativeElement;
    if (!element) {
      return;
    }

    const truncated = element.scrollHeight > element.clientHeight;
    if (this.isTruncated() !== truncated) {
      this.isTruncated.set(truncated);
    }
  }

  protected readonly faTrashCan = faTrashCan;

  protected readonly faEnvelopeOpen = faEnvelopeOpen;

  protected readonly faXmark = faXmark;
}
